import {
  Button,
  ModalityCodesEnum,
  SelectOptionType,
  addToast,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import DisplayError from '../../../../components/info/DisplayError';
import { SectionTitle } from '../../../../components/text';
import { useSearchAssistant } from '../../../../hooks/remoteSearchSelect/useSearchAssistant';
import { useSearchTeachers } from '../../../../hooks/remoteSearchSelect/useSearchTeachers';
import { useValidations } from '../../../../hooks/useValidations';
import { FormDataResponseType } from '../../../../types/sectionMaintainerTypes';
import { ContinuousEducationSectionValuesForm } from '../../types/ContinuousEducationSectionForm.types';
import {
  TeachersFields,
  AssistantsFields,
} from './ContinuousEducationSectionFormFields';
import { ContinuousEducationSectionGeneralFields } from './ContinuousEducationSectionGeneralFields';
import { useContinuousEducationSectionForm } from './useContinuousEducationSectionForm';
import { createContinuousEducationSectionRequest } from '../../api';

interface Props {
  onCancel: () => void;
  onSuccess: (id: number) => void;
  formData: FormDataResponseType | undefined;
  loading: boolean;
  error?: any;
  retry: () => void;
}

export function ContinuousEducationSectionForm(props: Props): JSX.Element {
  const {
    onCancel,
    onSuccess,
    formData,
    loading: loadingFormData,
    error: errorFormData,
    retry,
  } = props;
  const { t } = useTranslation();
  const prefix = 'sectionsMaintainer.create';

  const { msgValidations, rulesPatternNumeric } = useValidations();
  const searchTeachers = useSearchTeachers();
  const searchAssistants = useSearchAssistant();
  const methods = useForm<ContinuousEducationSectionValuesForm>({
    defaultValues: {
      teachers: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
      assistants: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
    },
  });
  const { control, setValue } = methods;
  const {
    coursesOptions,
    courseNamesOptions,
    courses,
    loadingCourses,
    facultyOptions,
    campusOptions,
    schedulesOptions,
    loadCourses,
  } = useContinuousEducationSectionForm(formData);
  const [selectedFaculty, selectedCourse] = useWatch({
    control,
    name: ['faculty', 'course'],
  });

  useEffect(() => {
    if (selectedFaculty?.value) {
      loadCourses(Number(selectedFaculty.value));
      setValue('course', null as any);
      setValue('courseName', null as any);
    } else {
      loadCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFaculty, loadCourses, setValue]);

  const currentCourse = useMemo(
    () => courses.find((c) => c.code === selectedCourse?.value),
    [courses, selectedCourse],
  );

  const currentDeliveryMode = useMemo(
    () =>
      currentCourse
        ? (currentCourse as any)?.modality?.deliveryMode || ModalityCodesEnum.PR
        : ModalityCodesEnum.PR,
    [currentCourse],
  );

  const supportsPresential = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.PR,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DU,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const supportsOnline = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.OL,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DI,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const onCourseChange = (option: SelectOptionType) => {
    setValue('course', option);
    if (option?.value) {
      const courseName = courseNamesOptions.find(
        (c) => (c as any).code === option.value,
      );
      if (courseName) {
        setValue('courseName', courseName);
      }
    } else {
      setValue('courseName', null as any);
    }
  };

  const onCourseNameChange = (option: SelectOptionType) => {
    setValue('courseName', option);
    if (option?.value) {
      const course = coursesOptions.find(
        (c) => c.value === (option as any).code,
      );
      if (course) {
        setValue('course', course);
      }
    } else {
      setValue('course', null as any);
    }
  };

  const onSubmit = async (values: ContinuousEducationSectionValuesForm) => {
    const activePeriod = formData?.periods?.find((p) => p.active);

    if (!activePeriod || !values.course?.value) {
      addToast({ text: t('common.error'), color: 'danger' });
      return;
    }

    const { data, error } = await createContinuousEducationSectionRequest({
      maximumStudent: Number(values.quota),
      campusId: Number(values.campus.value),
      courseCode: values.course.value.toString(),
      periodId: activePeriod.id,
      name: `${values.sectionCode}-${values.name}`,
      scheduleId: values.schedule?.value
        ? Number(values.schedule.value)
        : undefined,
      professors: values.teachers.filter((item) => !!item.value),
      assistants: values.assistants.filter((item) => !!item.value),
    });

    if (data && !error) {
      addToast({ text: t('common.saveSuccess'), color: 'success' });
      onSuccess(data.id);
    } else {
      const errorMessage = (error as any)?.data?.message;
      addToast({
        text: errorMessage || t('common.saveError'),
        color: 'danger',
      });
    }
  };

  if (errorFormData) {
    return (
      <DisplayError textBody={errorFormData.toString()} retryAction={retry} />
    );
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Row className="pb-4">
          <ContinuousEducationSectionGeneralFields
            control={control}
            facultyOptions={facultyOptions}
            coursesOptions={coursesOptions}
            courseNamesOptions={courseNamesOptions}
            campusOptions={campusOptions}
            schedulesOptions={schedulesOptions}
            msgValidations={msgValidations}
            rulesPatternNumeric={rulesPatternNumeric}
            loadingFormData={loadingFormData}
            loadingCourses={loadingCourses}
            t={t}
            prefix={prefix}
            onCourseChange={onCourseChange}
            onCourseNameChange={onCourseNameChange}
          />
        </Row>

        <Row className="pb-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.teacherTitle`)} />
          </Col>
          <TeachersFields
            t={t}
            prefix={prefix}
            searchTeachers={searchTeachers}
            supportsOnline={supportsOnline}
            supportsPresential={supportsPresential}
          />
          <AssistantsFields
            t={t}
            prefix={prefix}
            searchAssistants={searchAssistants}
            supportsOnline={supportsOnline}
            supportsPresential={supportsPresential}
          />
        </Row>

        <Row className="py-5 justify-content-end pt-5">
          <Col className="" xs={12} lg={3}>
            <Button
              outlined
              color="secondary"
              onClick={onCancel}
              className="mr-2"
              fullwidth
              text={t('common.terms.cancel')}
            />
          </Col>
          <Col xs={12} lg={3} className="pb-2 order-2 order-lg-1">
            <Button
              color="primary"
              type="submit"
              fullwidth
              text="+ Crear y continuar"
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}
